import { apiErrorSchema, type ApiErrorCode } from "@/contracts";

export interface ApiErrorDetail {
  field?: string | undefined;
  message: string;
}

/**
 * Every failed call surfaces as this, so screens branch on `code` instead of
 * matching on message text (section 8).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly details: ApiErrorDetail[];
  readonly requestId: string | null;

  constructor(init: {
    status: number;
    code: ApiErrorCode;
    message: string;
    details?: ApiErrorDetail[];
    requestId?: string | null;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.details = init.details ?? [];
    this.requestId = init.requestId ?? null;
  }

  /** Per-field messages, for wiring a failed validation back into a form. */
  fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {};

    for (const detail of this.details) {
      if (detail.field && !(detail.field in result)) {
        result[detail.field] = detail.message;
      }
    }

    return result;
  }
}

/**
 * Builds an ApiError from a response body.
 *
 * A body that does not match the error contract still has to produce
 * something usable: a proxy or a crash can return HTML, and the user should
 * see a plain message rather than a parse failure.
 */
export async function toApiError(response: Response): Promise<ApiError> {
  const fallback = {
    status: response.status,
    code: statusToCode(response.status),
    message: defaultMessage(response.status),
  };

  let payload: unknown;

  try {
    payload = await response.json();
  } catch {
    return new ApiError(fallback);
  }

  const parsed = apiErrorSchema.safeParse(payload);

  if (!parsed.success) {
    return new ApiError(fallback);
  }

  return new ApiError({
    status: response.status,
    code: parsed.data.error.code,
    message: parsed.data.error.message,
    details: parsed.data.error.details,
    requestId: parsed.data.error.requestId,
  });
}

function statusToCode(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return "BAD_REQUEST";
    case 401:
      return "UNAUTHENTICATED";
    case 403:
      return "FORBIDDEN";
    case 404:
      return "NOT_FOUND";
    case 409:
      return "CONFLICT";
    case 422:
      return "UNPROCESSABLE";
    case 429:
      return "RATE_LIMITED";
    case 503:
      return "SERVICE_UNAVAILABLE";
    default:
      return "INTERNAL_ERROR";
  }
}

function defaultMessage(status: number): string {
  if (status === 0) {
    return "We could not reach the server. Check your connection.";
  }

  if (status >= 500) {
    return "The server had a problem. Try again in a few minutes.";
  }

  return "We could not complete the operation.";
}

/** A request that never reached the API: offline, DNS, CORS or an aborted fetch. */
export function networkError(): ApiError {
  return new ApiError({
    status: 0,
    code: "SERVICE_UNAVAILABLE",
    message: defaultMessage(0),
  });
}
